import { Box, Flex, Group, Text } from '@mantine/core';
import { Communication } from '@medplum/fhirtypes';
import { describeClinicalTaskDue, getClinicalTaskDueDate } from '@bariendo/lib/clinical-tasks';
import { TaskGroupIdentifier } from '@bariendo/lib/constants';
import { IconFlagFilled } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { MRT_ColumnDef } from 'mantine-react-table';
import { ClinicalTaskIcon } from '../ClinicalTaskIcon';
import { CommunicationTypeIcon } from '../CommunicationTypeIcon';
import { HighlightableText } from '../HighlightableText';
import { RowActions } from './RowActions';
import classes from './RowActions.module.css';
import { NotificationItem, RowActionKind } from './types';
import { PriorityIcon } from '../PriorityIcon';

export function getNotificationColumns({
  action = RowActionKind.ARCHIVE,
  refresh,
  searchTerm = '',
}: {
  action?: RowActionKind;
  refresh?: () => void;
  searchTerm?: string;
}): MRT_ColumnDef<NotificationItem>[] {
  const isClinicalTaskRow = (row: NotificationItem): boolean =>
    row.task.groupIdentifier?.value === TaskGroupIdentifier.ORBIT_PROVIDER_TASK;

  return [
    {
      header: '',
      accessorKey: 'type',
      accessorFn: (row) => row.sourceCommunication,
      enableSorting: false,
      enableGlobalFilter: false,
      size: 20,
      Cell: ({ cell, row }) => {
        // Clinical tasks have no Communication focus — without this they fell
        // through to the gray unknown-type marker.
        if (isClinicalTaskRow(row.original)) {
          return <ClinicalTaskIcon />;
        }
        return <CommunicationTypeIcon communication={cell.getValue<Communication>()} />;
      },
    },
    {
      header: 'Patient',
      accessorFn: (row) => row.name,
      enableSorting: false,
      size: 150,
      Cell: ({ cell }) => {
        return (
          <HighlightableText text={cell.getValue<string>()} searchTerm={searchTerm} size="sm" c="dimmed" p="0px" />
        );
      },
    },
    {
      header: 'Message',
      accessorFn: (row) => row.content,
      size: 300,
      enableSorting: false,
      Cell: ({ cell, row }) => {
        const isHighPriorityTask = isClinicalTaskRow(row.original) && row.original.task.priority === 'urgent';
        return (
          <Group gap={6} wrap="nowrap" align="center">
            {/* High-priority flag leads the task name, mirroring the patient tab's card. */}
            {isHighPriorityTask && (
              <IconFlagFilled size={10} color="var(--mantine-color-error)" style={{ flexShrink: 0 }} />
            )}
            <HighlightableText
              text={cell.getValue<string>()}
              searchTerm={searchTerm}
              size="sm"
              c="dimmed"
              p="0px"
              style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}
            />
          </Group>
        );
      },
    },
    {
      header: 'Date',
      size: 120,
      accessorFn: (row) => row.sourceCommunication?.sent,
      Cell: ({ cell, row }) => {
        return (
          <>
            <Flex align="center">
              <Box
                className={classes.rowactions}
                px={10}
                top={10}
                style={{
                  zIndex: 1,
                  position: 'absolute',
                  right: 0,
                  marginRight: 20,
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  width: 'fit-content',
                  borderRadius: '10px',
                  border: '1px solid #e0e0e0',
                }}
                bg="white"
                py={5}
                onClick={(e) => e.stopPropagation()}
              >
                <RowActions notification={row.original} action={action} onRefresh={refresh} />
              </Box>
              {isClinicalTaskRow(row.original) ? (
                (() => {
                  // Tasks show their DUE date, not creation/sent time.
                  const dueDate = getClinicalTaskDueDate(row.original.task);
                  if (!dueDate) {
                    return (
                      <Text size="sm" c="dimmed">
                        No due date
                      </Text>
                    );
                  }
                  const due = describeClinicalTaskDue(dueDate);
                  return (
                    <Text
                      component="time"
                      size="sm"
                      style={{ whiteSpace: 'nowrap' }}
                      title={dayjs(dueDate).format('MMMM D, YYYY')}
                      c={due.overdue ? 'var(--mantine-color-error)' : 'dimmed'}
                    >
                      {due.label}
                    </Text>
                  );
                })()
              ) : (
                <Text size="sm" c="dimmed">
                  {dayjs(cell.getValue<Date>()).fromNow()}
                </Text>
              )}
            </Flex>
          </>
        );
      },
    },
    {
      header: '',
      accessorKey: 'priority',
      enableSorting: false,
      enableGlobalFilter: true,
      size: 20,
      Cell: ({ row }) => (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginRight: '16px' }}>
          <PriorityIcon priority={row.original.priorityStatus} />
        </div>
      ),
    },
  ];
}
